import { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Sidebar } from "@/components/layout/Sidebar";
import { Topbar } from "@/components/layout/Topbar";
import { cn } from "@/lib/utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { get } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { FuseauGate } from "@/components/layout/FuseauGate";

/** Glissement automatique de la démo (idempotent, ignoré côté serveur hors entreprise démo) + alignement sur le fuseau du présentateur. */
const useDemoSync = () => {
  const { isDemo, user, entrepriseId } = useAuth();
  const qc = useQueryClient();
  const enabled = isDemo || (user?.role === "superadmin" && !!entrepriseId);
  const fuseau = Intl.DateTimeFormat().resolvedOptions().timeZone;
  useQuery({
    queryKey: ["demo-sync", entrepriseId, fuseau], enabled, refetchInterval: 60000, refetchOnWindowFocus: true, retry: false,
    queryFn: async () => {
      const r = await get("/ext/demo/sync", { fuseau });
      if (r?.fuseau_change) { toast.info(`Usine démo alignée sur votre fuseau (${r.fuseau_change.nouveau}) — données regénérées`); qc.invalidateQueries(); }
      else if (r && r.skipped === false) qc.invalidateQueries({ queryKey: ["dashboard"] });
      return r;
    },
  });
};

export const AppLayout = () => {
  useDemoSync();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("prodtrack_sidebar") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setMobileOpen(false); }, [pathname]);
  const toggle = () => setCollapsed((c) => { localStorage.setItem("prodtrack_sidebar", c ? "0" : "1"); return !c; });
  return (
    <div className="flex h-screen overflow-hidden">
      {mobileOpen && <div data-testid="sidebar-backdrop" className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setMobileOpen(false)} />}
      <div className={cn("fixed inset-y-0 left-0 z-50 lg:static lg:z-auto transition-transform duration-200 lg:translate-x-0", mobileOpen ? "translate-x-0" : "-translate-x-full")}>
        <Sidebar collapsed={collapsed} onToggle={toggle} />
      </div>
      <div className="flex-1 flex flex-col min-w-0">
        <Topbar onMenu={() => setMobileOpen((o) => !o)} />
        <main data-testid="main-content" className="flex-1 overflow-auto p-3 sm:p-4">
          <FuseauGate><Outlet /></FuseauGate>
        </main>
      </div>
    </div>
  );
};
